import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import type { AxiosResponse } from 'axios';
import type ApiContentTutorialResponse from 'src/core/models/api/responses/ApiContentTutorialResponse';
import type ApiContentInspirationResponse from 'src/core/models/api/responses/ApiContentInspirationResponse';
import { reportOrRethrow } from 'src/core/utils/ApiCallbacks';
import toErrorMessage from 'src/core/utils/ErrorMessages';
import ThermenRepository from 'src/data/repositories/ThermenRepository';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

export const useContentStore = create(
	combine(
		{
			inspiration: null as ApiContentInspirationResponse | null,
			inspirationLoading: false,
			inspirationErrorMessage: null as string | null,
			tutorial: null as ApiContentTutorialResponse | null,
			tutorialLoading: false,
			tutorialErrorMessage: null as string | null,
		},
		(set, get) => {
			const getApiInspiration = async ({
				onSuccess,
				onError,
				onFinally,
			}: {
				onSuccess?: (response: AxiosResponse<ApiContentInspirationResponse>) => void | Promise<void>;
				onError?: (error: ApiRequestError) => void;
				onFinally?: () => void;
			} = {}) => {
				if (get().inspiration === null) {
					set({ inspirationLoading: true });
				}
				set({ inspirationErrorMessage: null });

				await ThermenRepository.getInspiration()
					.then(async (response) => {
						const data: ApiContentInspirationResponse | null = response.data;
						set({ inspiration: data });
						await onSuccess?.(response);
					})
					.catch((error: ApiRequestError) => {
						set({
							inspirationErrorMessage: toErrorMessage(error),
						});
						reportOrRethrow(error, onError);
					})
					.finally(() => {
						set({ inspirationLoading: false });
						onFinally?.();
					});
			};

			const getApiTutorial = async ({
				onSuccess,
				onError,
				onFinally,
			}: {
				onSuccess?: (response: AxiosResponse<ApiContentTutorialResponse>) => void | Promise<void>;
				onError?: (error: ApiRequestError) => void;
				onFinally?: () => void;
			} = {}) => {
				set({ tutorialLoading: true, tutorialErrorMessage: null });

				await ThermenRepository.getTutorial()
					.then(async (response) => {
						const data: ApiContentTutorialResponse | null = response.data;
						set({ tutorial: data });
						await onSuccess?.(response);
					})
					.catch((error: ApiRequestError) => {
						set({ tutorialErrorMessage: toErrorMessage(error) });
						reportOrRethrow(error, onError);
					})
					.finally(() => {
						set({ tutorialLoading: false });
						onFinally?.();
					});
			};

			return {
				getApiInspiration,
				getApiTutorial,
			};
		},
	),
);
